import React, { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import {
  BarChart3,
  Users,
  Star,
  ClipboardList,
  Settings as SettingsIcon,
  Loader2,
  RefreshCw,
  DollarSign,
  AlertTriangle,
  Clock,
  TrendingUp,
  TrendingDown,
  ChevronDown,
  ChevronRight,
  Save,
  Zap,
  ArrowUpDown,
  Target,
  MessageCircle,
  FileText,
  Download,
  PieChart,
  Bot,
  Shield,
  Sliders,
  Brain,
  Activity,
  Globe,
  Server,
  CheckCircle,
  Plus,
  X,
  Trash2,
  Eye,
  Film,
} from 'lucide-react';
import { C } from '../../components/design-system/primitives/constants';
import UNCRosterGrid from '../../components/unc/UNCRosterGrid';
import PortalIntelTab from '../../components/portal/PortalIntelTab';
import { CAROLINA_BLUE, ACCENT } from '../../config/uncConstants';
import AskSLAMAI from '../../components/chat/AskSLAMAI';
import UNCVideoHub from '../../components/unc/UNCVideoHub';

const CoachingCanvas = lazy(() => import('../../components/canvas/CoachingCanvas'));
const GameFilmDashboard = lazy(() => import('./GameFilmDashboard'));

const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'diamond-eye', label: 'Diamond Eye', icon: Eye },
  { id: 'ask-ai', label: 'Ask SLAM AI', icon: MessageCircle },
  { id: 'video-hub', label: 'Video Hub', icon: Film },
  { id: 'roster', label: 'Roster & Evaluations', icon: Users },
  { id: 'portal', label: 'Portal Intel', icon: Target },
  { id: 'box-scores', label: 'Box Scores', icon: ClipboardList },
  { id: 'agents', label: 'Agent Builder', icon: Bot },
  { id: 'settings', label: 'Settings', icon: SettingsIcon },
];

const AGGRESSIVENESS_OPTIONS = ['LOW', 'MEDIUM', 'HIGH'];

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

function KPICard({ label, value, subtext, icon: Icon, trend }) {
  return (
    <div
      className="rounded-xl p-4 transition-all duration-300"
      style={{ background: C.s1, border: `1px solid ${C.border}` }}
    >
      <div className="flex justify-between items-start mb-2">
        <span className="text-[9px] font-semibold tracking-widest uppercase" style={{ color: C.t3 }}>
          {label}
        </span>
        <Icon size={14} style={{ color: C.t3 }} />
      </div>
      <div className="text-2xl font-light tracking-tight" style={{ color: C.text }}>
        {value}
      </div>
      {subtext && (
        <div className="text-[11px] mt-1" style={{ color: C.t2 }}>
          {trend === 'up' && <TrendingUp size={10} className="inline mr-1" style={{ color: C.elite }} />}
          {trend === 'down' && <TrendingDown size={10} className="inline mr-1" style={{ color: C.dev }} />}
          {subtext}
        </div>
      )}
    </div>
  );
}

function formatCurrency(v) {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${(v || 0).toLocaleString()}`;
}

function fitColor(score) {
  if (score >= 70) return '#00CC66';
  if (score >= 40) return '#F59E0B';
  return '#FF4444';
}

/**
 * Staleness indicator for evaluation timestamps.
 * Returns { color, label, stale } based on age of evaluatedAt.
 */
function staleness(evaluatedAt) {
  if (!evaluatedAt) return { color: C.t3, label: 'Not yet analyzed', stale: null };
  const hoursAgo = (Date.now() - new Date(evaluatedAt).getTime()) / 3_600_000;
  if (hoursAgo < 24) return { color: '#00CC66', label: timeAgo(evaluatedAt), stale: false };
  if (hoursAgo < 48) return { color: '#F59E0B', label: timeAgo(evaluatedAt), stale: 'aging' };
  return { color: '#FF4444', label: timeAgo(evaluatedAt), stale: 'stale' };
}

function timeAgo(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/**
 * Parse evaluation response into { evalMap, evalTimestamp }.
 * Handles both array and object (latest report) shapes.
 */
function parseEvaluations(payload) {
  if (!payload) return { evalMap: {}, evalTimestamp: null };

  // Shape: { evaluatedAt, evaluations: [...] } — the "latest report" from auto-eval
  if (payload.evaluations && Array.isArray(payload.evaluations)) {
    const m = {};
    payload.evaluations.forEach((e) => { m[e.playerId || e.player_id] = e; });
    return { evalMap: m, evalTimestamp: payload.evaluatedAt || null };
  }

  // Shape: array of evaluations (direct)
  if (Array.isArray(payload)) {
    const m = {};
    payload.forEach((e) => { m[e.playerId || e.player_id] = e; });
    return { evalMap: m, evalTimestamp: null };
  }

  // Shape: already a map { [playerId]: evaluation }
  return { evalMap: payload, evalTimestamp: null };
}

// ═══════════════════════════════════════════════════════════════════════════
// OVERVIEW TAB
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// OVERVIEW TAB — GM COMMAND CENTER (single-viewport coaching dashboard)
// ═══════════════════════════════════════════════════════════════════════════

/** Format a batting average: 0.342 → ".342", 1.045 → "1.045" */
function fmtAvg(v) {
  if (v == null || v === '--') return '--';
  const n = Number(v);
  if (isNaN(n)) return '--';
  return n >= 1 ? n.toFixed(3) : n.toFixed(3).replace('0.', '.');
}

/** Mini SVG donut for win percentage */
function MiniDonut({ pct, size = 28 }) {
  const r = (size - 4) / 2;
  const circ = 2 * Math.PI * r;
  const filled = (Math.min(pct, 100) / 100) * circ;
  return (
    <svg width={size} height={size} className="flex-shrink-0">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={C.border} strokeWidth={3} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#10B981" strokeWidth={3}
        strokeDasharray={`${filled} ${circ}`} strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`} />
    </svg>
  );
}

/** Streak dots: "W6" → 6 green dots */
function StreakDots({ streak }) {
  if (!streak) return null;
  const type = streak[0];
  const count = parseInt(streak.slice(1), 10) || 0;
  const color = type === 'W' ? '#10B981' : type === 'L' ? '#FF4444' : C.t3;
  return (
    <div className="flex gap-1 mt-1.5">
      {Array.from({ length: Math.min(count, 12) }).map((_, i) => (
        <div key={i} className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
      ))}
    </div>
  );
}

/** Pitcher role pill: SP (blue), RP (muted), CL (green) */
function RolePill({ role }) {
  const styles = {
    SP: { bg: `${CAROLINA_BLUE}25`, color: CAROLINA_BLUE },
    RP: { bg: `${C.t3}25`, color: C.t2 },
    CL: { bg: '#10B98125', color: '#10B981' },
  };
  const s = styles[role] || styles.RP;
  return (
    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: s.bg, color: s.color }}>
      {role}
    </span>
  );
}

/** Single KPI tile in the top strip */
function DashKPI({ label, value, sub, trend, trendLabel, invertTrend, donutPct, streakDots }) {
  const hasTrend = trend != null && trend !== 0;
  const trendPositive = invertTrend ? trend < 0 : trend > 0;
  const trendColor = hasTrend ? (trendPositive ? '#10B981' : '#FF4444') : C.t3;

  return (
    <div className="rounded-xl p-3.5" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
      <span className="text-[9px] font-semibold tracking-[2px] uppercase block mb-1" style={{ color: C.t3 }}>
        {label}
      </span>
      <div className="flex items-center gap-2">
        {donutPct != null && <MiniDonut pct={donutPct} />}
        <span className="text-[28px] font-bold leading-none" style={{ color: C.text }}>{value}</span>
      </div>
      {streakDots && <StreakDots streak={streakDots} />}
      {hasTrend && (
        <div className="flex items-center gap-1 mt-1.5">
          {trendPositive
            ? <TrendingUp size={10} style={{ color: trendColor }} />
            : <TrendingDown size={10} style={{ color: trendColor }} />}
          <span className="text-[10px]" style={{ color: trendColor }}>
            {Math.abs(trend)} {trendLabel}
          </span>
        </div>
      )}
      {sub && !hasTrend && !streakDots && (
        <span className="text-[10px] block mt-1" style={{ color: C.t3 }}>{sub}</span>
      )}
    </div>
  );
}

function OverviewTab() {
  const [data, setData] = useState(null);
  const [dashLoading, setDashLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await fetch('/api/unc/gm/dashboard');
        if (res.ok && !cancelled) {
          const d = await res.json();
          setData(d?.data || d);
        }
      } catch (err) {
        console.error('[Dashboard] fetch error:', err);
      } finally {
        if (!cancelled) setDashLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  async function refresh() {
    setDashLoading(true);
    try {
      const res = await fetch('/api/unc/gm/dashboard');
      if (res.ok) {
        const d = await res.json();
        setData(d?.data || d);
      }
    } catch (err) {
      console.error('[Dashboard] refresh error:', err);
    } finally {
      setDashLoading(false);
    }
  }

  if (dashLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 size={24} className="animate-spin" style={{ color: C.t3 }} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-16">
        <Activity size={32} style={{ color: C.t3 }} className="mx-auto mb-3" />
        <p className="text-sm" style={{ color: C.t2 }}>Dashboard data unavailable</p>
        <button onClick={refresh} className="mt-3 text-xs px-3 py-1.5 rounded-lg"
          style={{ background: C.s2, color: C.carolinaBlue, border: `1px solid ${C.border}` }}>
          Retry
        </button>
      </div>
    );
  }

  const { teamKpis = {}, hittingLeaders = [], pitchingLeaders = [], playerFlags = [], schedule = [], insight } = data;

  return (
    <div className="space-y-4">
      {/* ── KPI STRIP — the "3-second glance" row ─────────────────────── */}
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
        <DashKPI label="RECORD" value={teamKpis.record || '--'} sub={teamKpis.winPct ? `${teamKpis.winPct}% win rate` : null} donutPct={teamKpis.winPct} />
        <DashKPI label="D1 RANK" value={teamKpis.rank ? `#${teamKpis.rank}` : '--'} />
        <DashKPI label="STREAK" value={teamKpis.streak || '--'} streakDots={teamKpis.streak} />
        <DashKPI label="RUNS/GM" value={teamKpis.runsPerGame ?? '--'} />
        <DashKPI label="TEAM ERA" value={teamKpis.teamERA ?? '--'} />
        <DashKPI label="OPP BA" value={teamKpis.oppBA != null ? fmtAvg(teamKpis.oppBA) : '--'} />
      </div>

      {/* ── LEADERS ROW ───────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Hitting Leaders */}
        <div className="rounded-xl overflow-hidden" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
          <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: `1px solid ${C.border}` }}>
            <span className="text-[9px] font-semibold tracking-[2px] uppercase" style={{ color: C.t3 }}>HITTING LEADERS</span>
            <span className="text-[9px]" style={{ color: C.t3 }}>by OPS</span>
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {['#', 'Player', 'AVG', 'OPS', 'HR', 'RBI'].map((h, i) => (
                  <th key={h} className={`px-3 py-2 ${i < 2 ? 'text-left' : 'text-right'} font-semibold text-[9px] tracking-[1.5px] uppercase`}
                    style={{ color: h === 'OPS' ? C.carolinaBlue : C.t3 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {hittingLeaders.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center" style={{ color: C.t3 }}>No batting data available</td></tr>
              )}
              {hittingLeaders.map((p, i) => (
                <tr key={i} style={{ background: i % 2 ? C.s2 : 'transparent' }}>
                  <td className="px-3 py-2.5 font-mono" style={{ color: C.t3 }}>{i + 1}</td>
                  <td className="px-3 py-2.5 font-medium" style={{ color: C.text }}>{p.name}</td>
                  <td className="px-3 py-2.5 text-right font-mono" style={{ color: C.t2 }}>{fmtAvg(p.avg)}</td>
                  <td className="px-3 py-2.5 text-right font-mono font-bold" style={{ color: C.carolinaBlue }}>{p.ops != null ? p.ops.toFixed(3) : '--'}</td>
                  <td className="px-3 py-2.5 text-right font-mono" style={{ color: C.t2 }}>{p.hr ?? '--'}</td>
                  <td className="px-3 py-2.5 text-right font-mono" style={{ color: C.t2 }}>{p.rbi ?? '--'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pitching Leaders */}
        <div className="rounded-xl overflow-hidden" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
          <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: `1px solid ${C.border}` }}>
            <span className="text-[9px] font-semibold tracking-[2px] uppercase" style={{ color: C.t3 }}>PITCHING LEADERS</span>
            <span className="text-[9px]" style={{ color: C.t3 }}>by ERA</span>
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {['#', 'Pitcher', 'ERA', 'IP', 'K', 'WHIP', 'Role'].map((h, i) => (
                  <th key={h} className={`px-3 py-2 ${i < 2 ? 'text-left' : 'text-right'} font-semibold text-[9px] tracking-[1.5px] uppercase`}
                    style={{ color: h === 'ERA' ? C.carolinaBlue : C.t3 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pitchingLeaders.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-6 text-center" style={{ color: C.t3 }}>No pitching data available</td></tr>
              )}
              {pitchingLeaders.map((p, i) => (
                <tr key={i} style={{ background: i % 2 ? C.s2 : 'transparent' }}>
                  <td className="px-3 py-2.5 font-mono" style={{ color: C.t3 }}>{i + 1}</td>
                  <td className="px-3 py-2.5 font-medium" style={{ color: C.text }}>{p.name}</td>
                  <td className="px-3 py-2.5 text-right font-mono font-bold" style={{ color: C.carolinaBlue }}>{p.era}</td>
                  <td className="px-3 py-2.5 text-right font-mono" style={{ color: C.t2 }}>{p.ip}</td>
                  <td className="px-3 py-2.5 text-right font-mono" style={{ color: C.t2 }}>{p.k}</td>
                  <td className="px-3 py-2.5 text-right font-mono" style={{ color: C.t2 }}>{p.whip}</td>
                  <td className="px-3 py-2.5 text-right"><RolePill role={p.role} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── BOTTOM ROW — Flags | Schedule | AI Insight ────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Player Flags */}
        <div className="rounded-xl overflow-hidden" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
          <div className="px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
            <span className="text-[9px] font-semibold tracking-[2px] uppercase" style={{ color: C.t3 }}>PLAYER FLAGS</span>
          </div>
          <div className="px-3 py-2 space-y-0.5">
            {playerFlags.length === 0 ? (
              <div className="py-4 text-center">
                <CheckCircle size={16} className="mx-auto mb-1" style={{ color: C.elite }} />
                <span className="text-xs" style={{ color: C.t3 }}>No active flags</span>
              </div>
            ) : playerFlags.slice(0, 8).map((f, i) => (
              <div key={i} className="flex items-start gap-2 py-2 px-2 rounded-lg" style={{ background: i % 2 ? C.s2 : 'transparent' }}>
                <span className="text-sm flex-shrink-0 leading-none mt-0.5">
                  {f.type === 'hot' ? '\uD83D\uDD25' : f.type === 'cold' ? '\uD83E\uDDCA' : '\u26A0\uFE0F'}
                </span>
                <div className="min-w-0">
                  <span className="text-xs font-medium block" style={{ color: C.text }}>{f.name}</span>
                  <span className="text-[10px] block mt-0.5" style={{ color: f.type === 'hot' ? '#EAB308' : f.type === 'cold' ? '#6B8DAD' : '#F59E0B' }}>
                    {f.detail}
                  </span>
                </div>
              </div>
            ))}
            {playerFlags.length > 8 && (
              <div className="text-[10px] text-center py-1 cursor-pointer" style={{ color: C.carolinaBlue }}>
                View all ({playerFlags.length})
              </div>
            )}
          </div>
        </div>

        {/* Next 5 Games */}
        <div className="rounded-xl overflow-hidden" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
          <div className="px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
            <span className="text-[9px] font-semibold tracking-[2px] uppercase" style={{ color: C.t3 }}>NEXT 5 GAMES</span>
          </div>
          <div>
            {schedule.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs" style={{ color: C.t3 }}>No upcoming games</div>
            ) : schedule.map((g, i) => {
              const gDate = g.date ? new Date(g.date) : null;
              const isToday = gDate && gDate.toDateString() === new Date().toDateString();
              const isAway = g.location === '@';
              return (
                <div key={i} className="flex items-center gap-3 px-4 py-2.5"
                  style={{ borderLeft: isToday ? `3px solid ${CAROLINA_BLUE}` : '3px solid transparent', borderBottom: `1px solid ${C.borderSubtle}` }}>
                  <span className="text-[10px] font-mono w-10 flex-shrink-0" style={{ color: C.t2 }}>
                    {gDate ? `${gDate.getMonth() + 1}/${gDate.getDate()}` : '--'}
                  </span>
                  <span className="text-[10px] font-bold w-5 flex-shrink-0" style={{ color: isAway ? C.t3 : CAROLINA_BLUE }}>
                    {isAway ? '@' : 'vs'}
                  </span>
                  <span className="text-xs font-medium flex-1 truncate" style={{ color: C.text }}>{g.opponent}</span>
                  {isToday && (
                    <span className="text-[8px] font-bold px-1.5 py-0.5 rounded flex-shrink-0" style={{ background: CAROLINA_BLUE, color: '#000' }}>
                      TODAY
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* SLAM AI Insight Card */}
        <div className="rounded-xl overflow-hidden relative"
          style={{ background: `linear-gradient(135deg, ${C.s1} 0%, ${C.carolinaBlueDim} 100%)`, border: `1px solid ${C.carolinaBlue}30` }}>
          <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: `1px solid ${C.carolinaBlue}20` }}>
            <Brain size={12} style={{ color: C.carolinaBlue }} />
            <span className="text-[9px] font-semibold tracking-[2px] uppercase" style={{ color: C.carolinaBlue }}>SLAM INSIGHT</span>
          </div>
          <div className="px-4 py-3">
            {insight?.text ? (
              <>
                <p className="text-[13px] leading-relaxed" style={{ color: C.text }}>{insight.text}</p>
                {insight.generatedAt && (
                  <p className="text-[9px] mt-3" style={{ color: C.t3 }}>
                    Generated {new Date(insight.generatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </p>
                )}
              </>
            ) : (
              <div className="flex items-center gap-2 py-2">
                <Loader2 size={12} className="animate-spin" style={{ color: C.carolinaBlue }} />
                <span className="text-xs" style={{ color: C.t3 }}>Generating insight...</span>
              </div>
            )}
          </div>
          <div className="absolute bottom-2 right-3 text-[10px] font-bold tracking-widest opacity-[0.07]" style={{ color: C.carolinaBlue }}>
            SLAM
          </div>
        </div>
      </div>

      {/* ── Refresh ───────────────────────────────────────────────────── */}
      <div className="flex justify-end">
        <button onClick={refresh} disabled={dashLoading}
          className="flex items-center gap-1.5 text-[10px] px-3 py-1.5 rounded-lg transition-all hover:brightness-125 disabled:opacity-50"
          style={{ color: C.t3, background: C.s2, border: `1px solid ${C.border}` }}>
          <RefreshCw size={10} className={dashLoading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// EVALUATIONS TAB
// ═══════════════════════════════════════════════════════════════════════════

function EvaluationsTab({ roster, evaluations, evalTimestamp, onTriggerEval, evalLoading, evalProgress }) {
  const [sortCol, setSortCol] = useState('fitScore');
  const [sortDir, setSortDir] = useState('desc');

  // Watchlist state
  const [watchlist, setWatchlist] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', school: '', position: '', notes: '' });
  const [addingPlayer, setAddingPlayer] = useState(false);

  // Fetch watchlist
  useEffect(() => {
    async function loadWatchlist() {
      try {
        const res = await fetch('/api/unc/watchlist');
        if (res.ok) {
          const d = await res.json();
          const payload = d?.data || d;
          setWatchlist(payload?.watchlist || []);
        }
      } catch (err) {
        console.error('[Watchlist] load error:', err);
      }
    }
    loadWatchlist();
  }, []);

  // Add to watchlist
  async function handleAddPlayer(e) {
    e.preventDefault();
    if (!addForm.name.trim() || !addForm.school.trim()) return;
    setAddingPlayer(true);
    try {
      const res = await fetch('/api/unc/watchlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(addForm),
      });
      if (res.ok) {
        const d = await res.json();
        const player = d?.data || d;
        setWatchlist((prev) => [...prev, player]);
        setAddForm({ name: '', school: '', position: '', notes: '' });
        setShowAddForm(false);
      }
    } catch (err) {
      console.error('[Watchlist] add error:', err);
    } finally {
      setAddingPlayer(false);
    }
  }

  // Remove from watchlist
  async function handleRemovePlayer(id) {
    try {
      await fetch(`/api/unc/watchlist/${id}`, { method: 'DELETE' });
      setWatchlist((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      console.error('[Watchlist] remove error:', err);
    }
  }

  // Build roster rows with evaluation data
  const rows = useMemo(() => {
    const list = (roster || []).map((p) => {
      const pid = p.id || p.playerId || p.player_id;
      const ev = evaluations[pid] || {};
      const innerEval = ev.evaluation || {};
      const hasEval = !!(ev.evaluatedAt || innerEval.slamFitScore || innerEval.slam_fit_score);
      return {
        ...p,
        pid,
        hasEval,
        fitScore: innerEval.slamFitScore ?? innerEval.slam_fit_score ?? ev.slamFitScore ?? ev.slam_fit_score ?? null,
        transferRisk: (innerEval.transferRisk ?? innerEval.transfer_risk ?? ev.transferRisk ?? ev.transfer_risk ?? '').toUpperCase() || null,
        characterScore: innerEval.characterScore ?? innerEval.character_score ?? ev.characterScore ?? ev.character_score ?? null,
        statsScore: innerEval.statsScore ?? innerEval.stats_score ?? ev.statsScore ?? ev.stats_score ?? null,
        mlbPotential: innerEval.mlbPotential ?? innerEval.mlb_potential ?? ev.mlbPotential ?? ev.mlb_potential ?? null,
        evaluatedAt: ev.evaluatedAt || null,
      };
    });

    list.sort((a, b) => {
      let cmp = 0;
      if (sortCol === 'name') cmp = (a.name || '').localeCompare(b.name || '');
      else if (sortCol === 'fitScore') cmp = (a.fitScore ?? -1) - (b.fitScore ?? -1);
      else if (sortCol === 'characterScore') cmp = (a.characterScore ?? -1) - (b.characterScore ?? -1);
      else if (sortCol === 'statsScore') cmp = (a.statsScore ?? -1) - (b.statsScore ?? -1);
      else if (sortCol === 'mlbPotential') cmp = (a.mlbPotential ?? -1) - (b.mlbPotential ?? -1);
      else if (sortCol === 'transferRisk') {
        const order = { HIGH: 0, MEDIUM: 1, LOW: 2 };
        cmp = (order[a.transferRisk] ?? 3) - (order[b.transferRisk] ?? 3);
      } else if (sortCol === 'staleness') {
        cmp = new Date(a.evaluatedAt || 0).getTime() - new Date(b.evaluatedAt || 0).getTime();
      }
      return sortDir === 'desc' ? -cmp : cmp;
    });

    return list;
  }, [roster, evaluations, sortCol, sortDir]);

  function toggleSort(col) {
    if (sortCol === col) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  }

  const globalStaleness = staleness(evalTimestamp);
  const analyzedCount = rows.filter((r) => r.hasEval).length;
  const thClass = 'text-left text-[9px] tracking-widest uppercase px-3 py-2.5 cursor-pointer select-none hover:text-white transition-colors';
  const riskColors = { LOW: '#00CC66', MEDIUM: '#F59E0B', HIGH: '#FF4444' };

  return (
    <div className="space-y-6">
      {/* Evaluation header with global staleness */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div>
            <div className="text-sm font-medium text-white">Player Evaluations</div>
            <div className="flex items-center gap-2 mt-0.5">
              <Clock size={10} style={{ color: globalStaleness.color }} />
              <span className="text-[10px]" style={{ color: globalStaleness.color }}>
                {evalTimestamp ? `Last full evaluation: ${globalStaleness.label}` : 'No evaluations run yet'}
              </span>
              <span className="text-[10px]" style={{ color: C.t3 }}>
                — {analyzedCount}/{rows.length} analyzed
              </span>
            </div>
          </div>
        </div>
        <button
          onClick={onTriggerEval}
          disabled={evalLoading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all hover:brightness-110 disabled:opacity-50"
          style={{ background: ACCENT, color: '#000' }}
        >
          {evalLoading ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
          {evalLoading
            ? evalProgress ? `${evalProgress.completed}/${evalProgress.total}` : 'Starting...'
            : 'Run Evaluation'}
        </button>
      </div>

      {/* Staleness warning banner */}
      {globalStaleness.stale === 'stale' && evalTimestamp && (
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs"
          style={{ background: '#FF444418', color: '#FF4444' }}
        >
          <AlertTriangle size={13} />
          Data is {timeAgo(evalTimestamp)} old — consider re-evaluating
        </div>
      )}
      {globalStaleness.stale === 'aging' && evalTimestamp && (
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs"
          style={{ background: '#F59E0B18', color: '#F59E0B' }}
        >
          <Clock size={13} />
          Evaluations are {timeAgo(evalTimestamp)} old — may need refresh
        </div>
      )}

      {/* Roster evaluations table */}
      <div className="overflow-x-auto rounded-xl border border-zinc-800">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: C.s1, color: C.t3 }}>
              <th className={thClass} onClick={() => toggleSort('name')}>
                Player <ArrowUpDown size={10} className="inline ml-1 opacity-50" />
              </th>
              <th className={thClass}>Pos</th>
              <th className={thClass} onClick={() => toggleSort('fitScore')}>
                SLAM Fit <ArrowUpDown size={10} className="inline ml-1 opacity-50" />
              </th>
              <th className={thClass} onClick={() => toggleSort('characterScore')}>
                Character <ArrowUpDown size={10} className="inline ml-1 opacity-50" />
              </th>
              <th className={thClass} onClick={() => toggleSort('statsScore')}>
                Stats <ArrowUpDown size={10} className="inline ml-1 opacity-50" />
              </th>
              <th className={thClass} onClick={() => toggleSort('mlbPotential')}>
                MLB Pot. <ArrowUpDown size={10} className="inline ml-1 opacity-50" />
              </th>
              <th className={thClass} onClick={() => toggleSort('transferRisk')}>
                Risk <ArrowUpDown size={10} className="inline ml-1 opacity-50" />
              </th>
              <th className={thClass} onClick={() => toggleSort('staleness')}>
                Analyzed <ArrowUpDown size={10} className="inline ml-1 opacity-50" />
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const s = staleness(r.evaluatedAt);
              return (
                <tr
                  key={r.pid || i}
                  className="border-t border-zinc-800/50 hover:bg-zinc-900/50 transition-colors"
                >
                  <td className="px-3 py-2.5 text-white font-medium">{r.name}</td>
                  <td className="px-3 py-2.5 text-zinc-400">{r.position}</td>
                  <td className="px-3 py-2.5">
                    {r.fitScore != null ? (
                      <span className="font-mono" style={{ color: fitColor(r.fitScore) }}>{Math.round(r.fitScore)}</span>
                    ) : (
                      <span style={{ color: C.t3 }}>--</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-zinc-400">{r.characterScore != null ? Math.round(r.characterScore) : '--'}</td>
                  <td className="px-3 py-2.5 text-zinc-400">{r.statsScore != null ? Math.round(r.statsScore) : '--'}</td>
                  <td className="px-3 py-2.5 text-zinc-400">{r.mlbPotential != null ? Math.round(r.mlbPotential) : '--'}</td>
                  <td className="px-3 py-2.5">
                    {r.transferRisk ? (
                      <span
                        className="text-[9px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full"
                        style={{
                          color: riskColors[r.transferRisk] || riskColors.LOW,
                          background: `${riskColors[r.transferRisk] || riskColors.LOW}18`,
                        }}
                      >
                        {r.transferRisk}
                      </span>
                    ) : (
                      <span style={{ color: C.t3 }}>--</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="text-[10px]" style={{ color: s.color }}>
                      {s.label}
                    </span>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center py-12 text-zinc-600 text-sm">No roster data available</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* WATCHLIST — External Players                                       */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <div className="rounded-xl border border-zinc-800 overflow-hidden" style={{ background: C.s1 }}>
        <div className="px-4 py-3 flex items-center justify-between border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <Eye size={14} style={{ color: CAROLINA_BLUE }} />
            <span className="text-xs font-semibold tracking-wide uppercase" style={{ color: C.t2 }}>
              Watchlist — External Players
            </span>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: `${CAROLINA_BLUE}20`, color: CAROLINA_BLUE }}>
              {watchlist.length}
            </span>
          </div>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium rounded-lg transition-colors"
            style={{ background: `${CAROLINA_BLUE}20`, color: CAROLINA_BLUE }}
          >
            {showAddForm ? <X size={10} /> : <Plus size={10} />}
            {showAddForm ? 'Cancel' : 'Add Player'}
          </button>
        </div>

        {/* Add player form */}
        {showAddForm && (
          <form onSubmit={handleAddPlayer} className="px-4 py-3 border-b border-zinc-800 flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[140px]">
              <label className="text-[9px] uppercase tracking-widest block mb-1" style={{ color: C.t3 }}>Name *</label>
              <input
                type="text"
                value={addForm.name}
                onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-600"
                placeholder="Player name"
                required
              />
            </div>
            <div className="flex-1 min-w-[140px]">
              <label className="text-[9px] uppercase tracking-widest block mb-1" style={{ color: C.t3 }}>School *</label>
              <input
                type="text"
                value={addForm.school}
                onChange={(e) => setAddForm((f) => ({ ...f, school: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-600"
                placeholder="University / school"
                required
              />
            </div>
            <div className="w-20">
              <label className="text-[9px] uppercase tracking-widest block mb-1" style={{ color: C.t3 }}>Pos</label>
              <input
                type="text"
                value={addForm.position}
                onChange={(e) => setAddForm((f) => ({ ...f, position: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-600"
                placeholder="P, SS..."
              />
            </div>
            <button
              type="submit"
              disabled={addingPlayer}
              className="px-3 py-1.5 text-xs font-medium rounded-lg transition-all hover:brightness-110 disabled:opacity-50"
              style={{ background: CAROLINA_BLUE, color: '#000' }}
            >
              {addingPlayer ? <Loader2 size={12} className="animate-spin" /> : 'Add'}
            </button>
          </form>
        )}

        {/* Watchlist table */}
        {watchlist.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: C.s2, color: C.t3 }}>
                <th className="text-left text-[9px] tracking-widest uppercase px-3 py-2">Player</th>
                <th className="text-left text-[9px] tracking-widest uppercase px-3 py-2">School</th>
                <th className="text-left text-[9px] tracking-widest uppercase px-3 py-2">Pos</th>
                <th className="text-left text-[9px] tracking-widest uppercase px-3 py-2">Added</th>
                <th className="text-left text-[9px] tracking-widest uppercase px-3 py-2">Status</th>
                <th className="text-right text-[9px] tracking-widest uppercase px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {watchlist.map((wp) => {
                const ws = staleness(wp.evaluatedAt);
                return (
                  <tr key={wp.id} className="border-t border-zinc-800/50 hover:bg-zinc-900/50 transition-colors">
                    <td className="px-3 py-2 text-white font-medium">{wp.name}</td>
                    <td className="px-3 py-2" style={{ color: CAROLINA_BLUE }}>{wp.school}</td>
                    <td className="px-3 py-2 text-zinc-400">{wp.position || '--'}</td>
                    <td className="px-3 py-2 text-zinc-500 text-[10px]">{wp.addedAt ? timeAgo(wp.addedAt) : '--'}</td>
                    <td className="px-3 py-2">
                      <span className="text-[10px]" style={{ color: ws.color }}>{ws.label}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => handleRemovePlayer(wp.id)}
                        className="text-zinc-600 hover:text-red-400 transition-colors p-1"
                        title="Remove from watchlist"
                      >
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="px-4 py-8 text-center text-zinc-600 text-xs">
            No external players on watchlist — add transfer portal candidates, recruits, or opponents
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// BOX SCORES TAB
// ═══════════════════════════════════════════════════════════════════════════

function BoxScoresTab({ boxScores, onRefresh }) {
  const [expanded, setExpanded] = useState(null);
  const [scraping, setScraping] = useState(false);
  const [scrapeResult, setScrapeResult] = useState(null);
  const games = boxScores || [];

  const handleAutoScrape = async () => {
    setScraping(true);
    setScrapeResult(null);
    try {
      const res = await fetch('/api/unc/box-scores/auto-scrape', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ maxScrapes: 10 }),
      });
      const data = await res.json();
      setScrapeResult(data);
      if (data.scraped > 0 && onRefresh) onRefresh();
    } catch (err) {
      setScrapeResult({ error: err.message });
    } finally {
      setScraping(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Auto-scrape action bar */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-zinc-500">{games.length} game{games.length !== 1 ? 's' : ''} stored</div>
        <button
          onClick={handleAutoScrape}
          disabled={scraping}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
          style={{
            background: scraping ? `${CAROLINA_BLUE}30` : `${CAROLINA_BLUE}20`,
            color: CAROLINA_BLUE,
          }}
        >
          <RefreshCw size={12} className={scraping ? 'animate-spin' : ''} />
          {scraping ? 'Discovering...' : 'Auto-Scrape New Games'}
        </button>
      </div>
      {scrapeResult && !scrapeResult.error && (
        <div className="text-xs px-3 py-2 rounded-lg" style={{ background: `${CAROLINA_BLUE}10`, color: CAROLINA_BLUE }}>
          Discovered {scrapeResult.discovered} games — scraped {scrapeResult.scraped} new, {scrapeResult.skipped} already stored
          {scrapeResult.errors > 0 && `, ${scrapeResult.errors} errors`}
        </div>
      )}
      {scrapeResult?.error && (
        <div className="text-xs px-3 py-2 rounded-lg bg-red-500/10 text-red-400">{scrapeResult.error}</div>
      )}
      {games.length === 0 && !scraping && (
        <div className="text-center py-16 text-zinc-600 text-sm">No box scores available — try Auto-Scrape</div>
      )}
      {games.map((g, i) => (
        <div key={g.id || i} className="rounded-xl border border-zinc-800 overflow-hidden" style={{ background: C.s1 }}>
          <button
            onClick={() => setExpanded(expanded === i ? null : i)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm text-zinc-300 hover:text-white transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="text-xs text-zinc-600">{g.date || 'TBD'}</span>
              <span className="font-medium text-white">{g.opponent || g.title || `Game ${i + 1}`}</span>
              {g.result && (
                <span
                  className="text-xs font-semibold px-1.5 py-0.5 rounded"
                  style={{
                    color: g.result.startsWith('W') ? C.elite : C.dev,
                    background: g.result.startsWith('W') ? `${C.elite}18` : `${C.dev}18`,
                  }}
                >
                  {g.result}
                </span>
              )}
            </div>
            {expanded === i ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>

          {expanded === i && (
            <div className="px-4 pb-4 border-t border-zinc-800 space-y-4">
              {/* Batting */}
              {g.batting && g.batting.length > 0 && (
                <div>
                  <div className="text-[9px] tracking-widest uppercase text-zinc-500 mt-3 mb-2">Batting</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr style={{ color: C.t3 }}>
                          <th className="text-left px-2 py-1">Player</th>
                          <th className="px-2 py-1">AB</th>
                          <th className="px-2 py-1">R</th>
                          <th className="px-2 py-1">H</th>
                          <th className="px-2 py-1">RBI</th>
                          <th className="px-2 py-1">BB</th>
                          <th className="px-2 py-1">SO</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.batting.map((b, j) => (
                          <tr key={j} className="border-t border-zinc-800/30">
                            <td className="text-left px-2 py-1 text-white">{b.name}</td>
                            <td className="text-center px-2 py-1 text-zinc-400">{b.ab ?? '--'}</td>
                            <td className="text-center px-2 py-1 text-zinc-400">{b.r ?? '--'}</td>
                            <td className="text-center px-2 py-1 text-zinc-400">{b.h ?? '--'}</td>
                            <td className="text-center px-2 py-1 text-zinc-400">{b.rbi ?? '--'}</td>
                            <td className="text-center px-2 py-1 text-zinc-400">{b.bb ?? '--'}</td>
                            <td className="text-center px-2 py-1 text-zinc-400">{b.so ?? '--'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Pitching */}
              {g.pitching && g.pitching.length > 0 && (
                <div>
                  <div className="text-[9px] tracking-widest uppercase text-zinc-500 mb-2">Pitching</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr style={{ color: C.t3 }}>
                          <th className="text-left px-2 py-1">Pitcher</th>
                          <th className="px-2 py-1">IP</th>
                          <th className="px-2 py-1">H</th>
                          <th className="px-2 py-1">ER</th>
                          <th className="px-2 py-1">BB</th>
                          <th className="px-2 py-1">K</th>
                          <th className="px-2 py-1">ERA</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.pitching.map((p, j) => (
                          <tr key={j} className="border-t border-zinc-800/30">
                            <td className="text-left px-2 py-1 text-white">{p.name}</td>
                            <td className="text-center px-2 py-1 text-zinc-400">{p.ip ?? '--'}</td>
                            <td className="text-center px-2 py-1 text-zinc-400">{p.h ?? '--'}</td>
                            <td className="text-center px-2 py-1 text-zinc-400">{p.er ?? '--'}</td>
                            <td className="text-center px-2 py-1 text-zinc-400">{p.bb ?? '--'}</td>
                            <td className="text-center px-2 py-1 text-zinc-400">{p.k ?? '--'}</td>
                            <td className="text-center px-2 py-1 text-zinc-400">{p.era ?? '--'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {!g.batting?.length && !g.pitching?.length && (
                <div className="text-xs text-zinc-600 py-3 text-center">Detailed stats not available</div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SETTINGS TAB — Comprehensive UNC Baseball Configuration
// ═══════════════════════════════════════════════════════════════════════════

const SETTINGS_SECTIONS = [
  { id: 'evaluation', label: 'Evaluation', icon: Sliders },
  { id: 'staff', label: 'Staff Directory', icon: Users },
  { id: 'portal', label: 'Portal Config', icon: Target },
  { id: 'reports', label: 'Reports', icon: FileText },
  { id: 'system', label: 'System', icon: Server },
];

const AGGRESSIVENESS_LABELS = { LOW: 'Conservative', MEDIUM: 'Balanced', HIGH: 'Aggressive' };

const REPORT_TYPES = [
  { id: 'roster-overview', label: 'Roster Overview', desc: 'Full roster with SLAM Fit scores, positions, stats', icon: Users },
  { id: 'evaluation-summary', label: 'Evaluation Summary', desc: 'AI evaluations, draft projections, transfer risk', icon: Star },
  { id: 'box-score-digest', label: 'Box Score Digest', desc: 'Season performance metrics from box scores', icon: ClipboardList },
  { id: 'portal-intel', label: 'Portal Intelligence', desc: 'Transfer portal candidates with fit analysis', icon: Target },
  { id: 'composite-report', label: 'Composite Report', desc: 'Full 8-lens composite score report (PDF/PPTX)', icon: PieChart },
];

const TIER_COLORS = {
  COACHING: CAROLINA_BLUE,
  SCOUTING: '#F59E0B',
  EXECUTIVE: ACCENT,
  OPERATIONS: '#60A5FA',
  MEDICAL: '#4ADE80',
  DEVELOPMENT: '#C084FC',
};

function SettingsTab({ settings, onSave, saving }) {
  const [section, setSection] = useState('evaluation');
  const [form, setForm] = useState({
    nilBudget: 0,
    characterWeight: 25,
    programFitWeight: 25,
    statsWeight: 25,
    mlbPotentialWeight: 25,
    mlbDraftTolerance: false,
    transferPortalAggressiveness: 'MEDIUM',
    ...settings,
  });
  const [saved, setSaved] = useState(false);

  // Staff directory
  const [staff, setStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffMeta, setStaffMeta] = useState(null);

  // Portal config
  const [portalConfig, setPortalConfig] = useState({ cultureContext: '', statsContext: '', conferenceFilter: '' });
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalSaving, setPortalSaving] = useState(false);

  // Reports
  const [generating, setGenerating] = useState(null);
  const [reportMsg, setReportMsg] = useState(null);

  useEffect(() => {
    if (settings) setForm((prev) => ({ ...prev, ...settings }));
  }, [settings]);

  // Lazy-load section data
  useEffect(() => {
    if (section === 'staff' && staff.length === 0) loadStaff();
    if (section === 'portal' && !portalLoading && !portalConfig._loaded) loadPortal();
  }, [section]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadStaff() {
    setStaffLoading(true);
    try {
      const res = await fetch('/api/unc/staff');
      if (res.ok) {
        const d = await res.json();
        const payload = d?.data || d;
        setStaff(payload?.staff || []);
        setStaffMeta({ org: payload?.organization, team: payload?.team_name, conference: payload?.conference, rosterCount: payload?.roster_player_count, scrapedAt: payload?.scraped_at });
      }
    } catch (err) { console.error('[Settings] staff load:', err); }
    finally { setStaffLoading(false); }
  }

  async function loadPortal() {
    setPortalLoading(true);
    try {
      const res = await fetch('/api/settings/portal-config');
      if (res.ok) {
        const d = await res.json();
        const cfg = d?.data?.baseball || d?.baseball || {};
        setPortalConfig({ cultureContext: cfg.cultureContext || '', statsContext: cfg.statsContext || '', conferenceFilter: cfg.conferenceFilter || '', _loaded: true });
      } else {
        setPortalConfig((p) => ({ ...p, _loaded: true }));
      }
    } catch (err) {
      console.error('[Settings] portal config load:', err);
      setPortalConfig((p) => ({ ...p, _loaded: true }));
    }
    finally { setPortalLoading(false); }
  }

  async function savePortalConfig() {
    setPortalSaving(true);
    try {
      const res = await fetch('/api/settings/portal-config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ baseball: { enabled: true, cultureContext: portalConfig.cultureContext, statsContext: portalConfig.statsContext, conferenceFilter: portalConfig.conferenceFilter } }),
      });
      if (res.ok) { setSaved(true); setTimeout(() => setSaved(false), 2500); }
    } catch (err) { console.error('[Settings] portal save:', err); }
    finally { setPortalSaving(false); }
  }

  async function handleSave() {
    onSave(form);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  async function generateReport(type) {
    setGenerating(type);
    setReportMsg(null);
    try {
      if (type === 'composite-report') {
        const res = await fetch('/api/basketball/export/template/pdf');
        if (!res.ok) throw new Error('Failed to generate composite report');
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `UNC_Composite_Report_${new Date().toISOString().split('T')[0]}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        setReportMsg({ type: 'success', text: 'Report downloaded' });
      } else {
        const endpoints = { 'roster-overview': '/api/unc/roster', 'evaluation-summary': '/api/unc/evaluations', 'box-score-digest': '/api/unc/box-scores', 'portal-intel': '/api/transfer-portal/search?sport=baseball&conference=ACC' };
        const res = await fetch(endpoints[type]);
        if (!res.ok) throw new Error(`Failed to fetch ${type}`);
        const json = await res.json();
        const data = json?.data || json;
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `UNC_${type}_${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
        setReportMsg({ type: 'success', text: 'Report downloaded' });
      }
    } catch (err) {
      setReportMsg({ type: 'error', text: err.message });
    } finally {
      setGenerating(null);
      setTimeout(() => setReportMsg(null), 3000);
    }
  }

  const weightSum = form.characterWeight + form.programFitWeight + form.statsWeight + form.mlbPotentialWeight;

  function update(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  // ── Section Renderers ──

  const renderEvaluation = () => (
    <div className="space-y-5">
      {/* NIL Budget */}
      <div className="rounded-xl p-5" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
        <div className="text-[9px] font-semibold tracking-widest uppercase mb-4" style={{ color: C.t3 }}>NIL Budget</div>
        <div className="relative">
          <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: C.t3 }} />
          <input
            type="text"
            value={(form.nilBudget || 0).toLocaleString()}
            onChange={(e) => update('nilBudget', parseInt(e.target.value.replace(/[^0-9]/g, '')) || 0)}
            className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg focus:outline-none transition-colors"
            style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }}
          />
        </div>
      </div>

      {/* Weight Configuration */}
      <div className="rounded-xl p-5" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
        <div className="flex items-center justify-between mb-4">
          <div className="text-[9px] font-semibold tracking-widest uppercase" style={{ color: C.t3 }}>SLAM Fit Weights</div>
          <span
            className="text-xs font-mono px-2 py-0.5 rounded"
            style={{
              color: Math.abs(weightSum - 100) <= 5 ? C.elite : C.dev,
              background: Math.abs(weightSum - 100) <= 5 ? `${C.elite}18` : `${C.dev}18`,
            }}
          >
            {weightSum}%
          </span>
        </div>
        <div className="space-y-4">
          {[
            { key: 'characterWeight', label: 'Character' },
            { key: 'programFitWeight', label: 'Program Fit' },
            { key: 'statsWeight', label: 'Stats' },
            { key: 'mlbPotentialWeight', label: 'MLB Potential' },
          ].map(({ key, label }) => (
            <div key={key}>
              <div className="flex justify-between items-center mb-1.5">
                <span className="text-xs" style={{ color: C.t2 }}>{label}</span>
                <span className="text-xs font-mono" style={{ color: C.text }}>{form[key]}%</span>
              </div>
              <input type="range" min={0} max={100} value={form[key]} onChange={(e) => update(key, parseInt(e.target.value))} className="w-full accent-[#7BAFD4] h-1.5" />
            </div>
          ))}
        </div>
      </div>

      {/* Toggles & Selectors */}
      <div className="rounded-xl p-5 space-y-4" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
        <div className="text-[9px] font-semibold tracking-widest uppercase" style={{ color: C.t3 }}>Acquisition Strategy</div>

        {/* MLB Draft Tolerance */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs" style={{ color: C.text }}>MLB Draft Tolerance</div>
            <div className="text-[10px] mt-0.5" style={{ color: C.t3 }}>Allow players with draft eligibility</div>
          </div>
          <button
            onClick={() => update('mlbDraftTolerance', !form.mlbDraftTolerance)}
            className="relative w-11 h-6 rounded-full transition-colors duration-300"
            style={{ background: form.mlbDraftTolerance ? ACCENT : C.s3 }}
          >
            <div className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-300" style={{ transform: form.mlbDraftTolerance ? 'translateX(22px)' : 'translateX(2px)' }} />
          </button>
        </div>

        {/* Aggressiveness */}
        <div>
          <div className="text-xs mb-2" style={{ color: C.text }}>Transfer Portal Aggressiveness</div>
          <div className="flex gap-2">
            {AGGRESSIVENESS_OPTIONS.map((o) => (
              <button
                key={o}
                onClick={() => update('transferPortalAggressiveness', o)}
                className="flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all"
                style={form.transferPortalAggressiveness === o
                  ? { background: `${ACCENT}20`, color: ACCENT, border: `1px solid ${ACCENT}40` }
                  : { background: C.bg, color: C.t3, border: `1px solid ${C.border}` }
                }
              >
                {o}
                <div className="text-[9px] mt-0.5 opacity-60">{AGGRESSIVENESS_LABELS[o]}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Save */}
      <button
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 hover:brightness-110 disabled:opacity-50"
        style={{ background: ACCENT, color: '#000' }}
      >
        {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
        {saving ? 'Saving...' : 'Save Configuration'}
      </button>
    </div>
  );

  const renderStaff = () => {
    if (staffLoading) return <div className="flex items-center justify-center py-16"><Loader2 size={20} className="animate-spin" style={{ color: C.t3 }} /></div>;
    return (
      <div className="space-y-4">
        {/* Org header */}
        {staffMeta && (
          <div className="rounded-xl p-4" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm" style={{ background: CAROLINA_BLUE, color: '#000' }}>UNC</div>
              <div>
                <div className="text-sm font-medium" style={{ color: C.text }}>{staffMeta.team} Baseball</div>
                <div className="text-[10px]" style={{ color: C.t3 }}>{staffMeta.conference} · {staffMeta.rosterCount} players · {staff.length} staff</div>
              </div>
            </div>
          </div>
        )}

        {/* Staff table */}
        <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${C.border}` }}>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: C.s1, color: C.t3 }}>
                <th className="text-left text-[9px] tracking-widest uppercase px-4 py-2.5">Name</th>
                <th className="text-left text-[9px] tracking-widest uppercase px-4 py-2.5">Title</th>
                <th className="text-left text-[9px] tracking-widest uppercase px-4 py-2.5">Tier</th>
                <th className="text-left text-[9px] tracking-widest uppercase px-4 py-2.5">Access</th>
              </tr>
            </thead>
            <tbody>
              {staff.map((s, i) => (
                <tr key={i} className="border-t border-zinc-800/50 hover:bg-zinc-900/50 transition-colors">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <div
                        className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold"
                        style={{ background: `${TIER_COLORS[s.tier] || C.t3}18`, color: TIER_COLORS[s.tier] || C.t3 }}
                      >
                        {s.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                      <span className="text-white font-medium text-xs">{s.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-xs" style={{ color: C.t2 }}>{s.title}</td>
                  <td className="px-4 py-2.5">
                    <span
                      className="text-[9px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full"
                      style={{ color: TIER_COLORS[s.tier] || C.t3, background: `${TIER_COLORS[s.tier] || C.t3}15` }}
                    >
                      {s.tier}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="text-[10px] font-mono" style={{ color: s.access_level === 'ADMIN' ? C.elite : C.t3 }}>
                      {s.access_level}
                    </span>
                  </td>
                </tr>
              ))}
              {staff.length === 0 && (
                <tr><td colSpan={4} className="text-center py-12 text-zinc-600 text-sm">No staff data available</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {staffMeta?.scrapedAt && (
          <div className="text-[10px] text-right" style={{ color: C.t3 }}>
            Source: goheels.com · Last updated {new Date(staffMeta.scrapedAt).toLocaleDateString()}
          </div>
        )}
      </div>
    );
  };

  const renderPortal = () => {
    if (portalLoading) return <div className="flex items-center justify-center py-16"><Loader2 size={20} className="animate-spin" style={{ color: C.t3 }} /></div>;
    return (
      <div className="space-y-5">
        <div className="rounded-xl p-5" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
          <div className="text-[9px] font-semibold tracking-widest uppercase mb-1" style={{ color: C.t3 }}>Culture Context</div>
          <div className="text-[10px] mb-3" style={{ color: C.t3 }}>Injected into every AI portal evaluation and player research</div>
          <textarea
            value={portalConfig.cultureContext}
            onChange={(e) => setPortalConfig((p) => ({ ...p, cultureContext: e.target.value }))}
            placeholder="Describe your program's culture, values, and what you look for in transfers..."
            rows={4}
            maxLength={2000}
            className="w-full rounded-lg px-3 py-2.5 text-xs resize-y focus:outline-none transition-colors"
            style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }}
          />
          <div className="text-[9px] text-right mt-1" style={{ color: C.t3 }}>{(portalConfig.cultureContext || '').length}/2000</div>
        </div>

        <div className="rounded-xl p-5" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
          <div className="text-[9px] font-semibold tracking-widest uppercase mb-1" style={{ color: C.t3 }}>Stats & Scheme Context</div>
          <div className="text-[10px] mb-3" style={{ color: C.t3 }}>Shapes how SLAM AI evaluates player statistical profiles</div>
          <textarea
            value={portalConfig.statsContext}
            onChange={(e) => setPortalConfig((p) => ({ ...p, statsContext: e.target.value }))}
            placeholder="Describe your stats priorities (ERA, OPS, K rate), offensive/defensive philosophy..."
            rows={4}
            maxLength={2000}
            className="w-full rounded-lg px-3 py-2.5 text-xs resize-y focus:outline-none transition-colors"
            style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }}
          />
          <div className="text-[9px] text-right mt-1" style={{ color: C.t3 }}>{(portalConfig.statsContext || '').length}/2000</div>
        </div>

        <div className="rounded-xl p-5" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
          <div className="text-[9px] font-semibold tracking-widest uppercase mb-3" style={{ color: C.t3 }}>Conference Filter</div>
          <input
            type="text"
            value={portalConfig.conferenceFilter}
            onChange={(e) => setPortalConfig((p) => ({ ...p, conferenceFilter: e.target.value }))}
            placeholder="e.g., ACC, Big 12, SEC (leave empty for all)"
            className="w-full rounded-lg px-3 py-2.5 text-xs focus:outline-none transition-colors"
            style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }}
          />
        </div>

        <button
          onClick={savePortalConfig}
          disabled={portalSaving}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 hover:brightness-110 disabled:opacity-50"
          style={{ background: ACCENT, color: '#000' }}
        >
          {portalSaving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
          {portalSaving ? 'Saving...' : 'Save Portal Config'}
        </button>
      </div>
    );
  };

  const renderReports = () => (
    <div className="space-y-3">
      {reportMsg && (
        <div className={`p-2.5 rounded-lg text-xs ${reportMsg.type === 'error' ? 'bg-red-500/10 border border-red-500/20 text-red-400' : 'bg-green-500/10 border border-green-500/20 text-green-400'}`}>
          {reportMsg.text}
        </div>
      )}
      {REPORT_TYPES.map((r) => {
        const Icon = r.icon;
        const isGen = generating === r.id;
        return (
          <div key={r.id} className="flex items-center justify-between p-4 rounded-xl transition-colors" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
            <div className="flex items-center gap-3 min-w-0">
              <Icon size={16} style={{ color: C.t3 }} />
              <div className="min-w-0">
                <div className="text-xs font-medium" style={{ color: C.text }}>{r.label}</div>
                <div className="text-[10px] truncate" style={{ color: C.t3 }}>{r.desc}</div>
              </div>
            </div>
            <button
              onClick={() => generateReport(r.id)}
              disabled={!!generating}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-40"
              style={{ background: `${ACCENT}20`, color: ACCENT }}
            >
              {isGen ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
              {isGen ? 'Generating...' : 'Export'}
            </button>
          </div>
        );
      })}
    </div>
  );

  const renderSystem = () => (
    <div className="space-y-4">
      <div className="rounded-xl p-5" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
        <div className="text-[9px] font-semibold tracking-widest uppercase mb-4" style={{ color: C.t3 }}>Platform</div>
        <div className="space-y-3">
          {[
            { label: 'Organization', value: 'University of North Carolina', icon: Globe },
            { label: 'Sport', value: 'College Baseball (D1 · ACC)', icon: Activity },
            { label: 'AI Model (Execution)', value: 'Gemini 3.1 Pro Preview', icon: Brain },
            { label: 'AI Model (Orchestration)', value: 'Claude Opus 4.6', icon: Brain },
            { label: 'Video Analysis', value: 'Gemini 3.1 Pro + Multimodal Live', icon: BarChart3 },
            { label: 'Deployment', value: 'Google Cloud Run (slam-unc)', icon: Server },
          ].map(({ label, value, icon: Icon }) => (
            <div key={label} className="flex items-center justify-between py-1.5">
              <div className="flex items-center gap-2">
                <Icon size={12} style={{ color: C.t3 }} />
                <span className="text-[10px] tracking-widest uppercase" style={{ color: C.t3 }}>{label}</span>
              </div>
              <span className="text-xs font-mono" style={{ color: C.text }}>{value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl p-5" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
        <div className="text-[9px] font-semibold tracking-widest uppercase mb-4" style={{ color: C.t3 }}>Data Sources</div>
        <div className="space-y-3">
          {[
            { label: 'Roster', value: 'goheels.com (scraped)', status: true },
            { label: 'Box Scores', value: 'StatBroadcast', status: true },
            { label: 'Evaluations', value: 'Gemini 3.1 Pro (auto 6AM/6PM ET)', status: true },
            { label: 'Player News', value: 'Exa Semantic Search', status: true },
            { label: 'Transfer Portal', value: 'BigQuery + NCAA APIs', status: true },
            { label: 'Stats Pipeline', value: 'Baseball Savant (Statcast)', status: true },
          ].map(({ label, value, status }) => (
            <div key={label} className="flex items-center justify-between py-1.5">
              <span className="text-xs" style={{ color: C.t2 }}>{label}</span>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono" style={{ color: C.t3 }}>{value}</span>
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: status ? '#22C55E' : '#EF4444' }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const sectionContent = {
    evaluation: renderEvaluation,
    staff: renderStaff,
    portal: renderPortal,
    reports: renderReports,
    system: renderSystem,
  };

  return (
    <div className="space-y-5">
      {/* Sub-tab bar */}
      <div className="flex items-center gap-1 pb-1" style={{ borderBottom: `1px solid ${C.border}` }}>
        {SETTINGS_SECTIONS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setSection(id)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all"
            style={section === id
              ? { background: `${ACCENT}15`, color: ACCENT, border: `1px solid ${ACCENT}30` }
              : { color: C.t3 }
            }
          >
            <Icon size={12} />
            {label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          {saved && (
            <div className="flex items-center gap-1.5 text-xs" style={{ color: '#22C55E' }}>
              <CheckCircle size={13} /> Saved
            </div>
          )}
        </div>
      </div>

      {/* Section content */}
      <div className="max-w-2xl">
        {sectionContent[section]?.()}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// UNCGMDashboard
// ═══════════════════════════════════════════════════════════════════════════

export default function UNCGMDashboard({ initialTab }) {
  const [tab, setTab] = useState(initialTab || 'overview');
  const [loading, setLoading] = useState(true);

  // Data
  const [overview, setOverview] = useState(null);
  const [roster, setRoster] = useState([]);
  const [evaluations, setEvaluations] = useState({});
  const [evalTimestamp, setEvalTimestamp] = useState(null);
  const [boxScores, setBoxScores] = useState([]);
  const [settings, setSettings] = useState(null);

  // UI states
  const [saving, setSaving] = useState(false);
  const [evalLoading, setEvalLoading] = useState(false);
  const [evalProgress, setEvalProgress] = useState(null); // { completed, total, currentPlayer }

  // ── Initial fetch ──
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const [ovRes, rosRes, evalRes, bsRes, setRes] = await Promise.all([
          fetch('/api/unc/gm/overview').catch(() => null),
          fetch('/api/unc/roster').catch(() => null),
          fetch('/api/unc/evaluations').catch(() => null),
          fetch('/api/unc/box-scores').catch(() => null),
          fetch('/api/unc/gm/settings').catch(() => null),
        ]);

        if (cancelled) return;

        if (ovRes?.ok) {
          const d = await ovRes.json();
          setOverview(d?.data || d);
        }
        if (rosRes?.ok) {
          const d = await rosRes.json();
          const payload = d?.data || d;
          setRoster(Array.isArray(payload) ? payload : payload?.players || []);
        }
        if (evalRes?.ok) {
          const d = await evalRes.json();
          const payload = d?.data || d;
          const { evalMap, evalTimestamp: ts } = parseEvaluations(payload);
          setEvaluations(evalMap);
          setEvalTimestamp(ts);
        }
        if (bsRes?.ok) {
          const d = await bsRes.json();
          const payload = d?.data || d;
          setBoxScores(Array.isArray(payload) ? payload : payload?.games || []);
        }
        if (setRes?.ok) {
          const d = await setRes.json();
          setSettings(d?.data || d);
        }
      } catch (err) {
        console.error('[UNCGMDashboard] load error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, []);

  // ── Refresh box scores (after auto-scrape) ──
  async function refreshBoxScores() {
    try {
      const res = await fetch('/api/unc/box-scores');
      if (res.ok) {
        const d = await res.json();
        setBoxScores(Array.isArray(d) ? d : d.games || []);
      }
    } catch (err) {
      console.error('[UNCGMDashboard] box scores refresh error:', err);
    }
  }

  // ── Save settings ──
  async function handleSaveSettings(form) {
    setSaving(true);
    try {
      const res = await fetch('/api/unc/gm/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        const d = await res.json();
        setSettings(d.settings || form);
      }
    } catch (err) {
      console.error('[UNCGMDashboard] save settings error:', err);
    } finally {
      setSaving(false);
    }
  }

  // ── Trigger evaluation (fire-and-forget with polling) ──
  async function handleTriggerEval() {
    setEvalLoading(true);
    setEvalProgress(null);
    try {
      const res = await fetch('/api/unc/evaluations/trigger', { method: 'POST' });
      if (!res.ok) {
        console.error('[UNCGMDashboard] trigger eval failed:', res.status);
        setEvalLoading(false);
        return;
      }

      // Poll for progress every 5 seconds
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await fetch('/api/unc/evaluations/status');
          if (!statusRes.ok) return;
          const job = await statusRes.json();
          const jobData = job?.data || job;

          setEvalProgress({
            completed: (jobData.completed || 0) + (jobData.failed || 0),
            total: jobData.total || 0,
            currentPlayer: jobData.currentPlayer || null,
          });

          if (jobData.status === 'complete' || jobData.status === 'error') {
            clearInterval(pollInterval);
            setEvalLoading(false);
            setEvalProgress(null);

            // Refresh evaluations + overview
            const [evalRes, ovRes] = await Promise.all([
              fetch('/api/unc/evaluations'),
              fetch('/api/unc/gm/overview'),
            ]);
            if (evalRes.ok) {
              const d = await evalRes.json();
              const payload = d?.data || d;
              const { evalMap, evalTimestamp: ts } = parseEvaluations(payload);
              setEvaluations(evalMap);
              setEvalTimestamp(ts);
            }
            if (ovRes.ok) {
              const d = await ovRes.json();
              setOverview(d?.data || d);
            }
          }
        } catch (err) {
          console.error('[UNCGMDashboard] poll eval status error:', err);
        }
      }, 5000);

      // Safety timeout: stop polling after 60 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        setEvalLoading(false);
        setEvalProgress(null);
      }, 60 * 60 * 1000);

    } catch (err) {
      console.error('[UNCGMDashboard] trigger eval error:', err);
      setEvalLoading(false);
    }
  }

  // ── Render ──
  return (
    <div className="h-full flex flex-col bg-black">
      {/* Header */}
      <div className="shrink-0 px-5 pt-5 pb-3">
        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm"
            style={{ background: CAROLINA_BLUE, color: '#000' }}
          >
            UNC
          </div>
          <div>
            <h1 className="text-lg font-semibold text-white">UNC Baseball -- GM Dashboard</h1>
            <p className="text-xs text-zinc-500">Tar Heels program management</p>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all duration-200 ${
                tab === id
                  ? 'text-black'
                  : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-900'
              }`}
              style={tab === id ? { background: ACCENT } : undefined}
            >
              <Icon size={13} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-5 pb-5" style={{ scrollbarWidth: 'thin' }}>
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 size={24} className="animate-spin text-zinc-600" />
          </div>
        ) : (
          <>
            {tab === 'overview' && (
              <OverviewTab />
            )}
            {tab === 'ask-ai' && (
              <div className="-mx-5 -mb-5 flex-1" style={{ minHeight: 'calc(100vh - 140px)' }}>
                <AskSLAMAI
                  sport="baseball"
                  sportCategory="baseball"
                  leagueCode="CBB"
                  teamName="UNC Tar Heels"
                  conference="ACC"
                  endpoint="/api/chat/context/stream"
                  accentColor="orange"
                  enableAgentBuilder
                />
              </div>
            )}
            {tab === 'diamond-eye' && (
              <Suspense fallback={<div className="flex items-center justify-center h-64 text-zinc-500"><Loader2 className="animate-spin mr-2" size={20} /> Loading Diamond Eye...</div>}>
                <div style={{ height: 'calc(100vh - 200px)', borderRadius: 16, overflow: 'hidden', border: `1px solid ${C.border}` }}>
                  <GameFilmDashboard embedded />
                </div>
              </Suspense>
            )}
            {tab === 'video-hub' && <UNCVideoHub />}
            {tab === 'roster' && <UNCRosterGrid />}
            {tab === 'portal' && <PortalIntelTab sport="baseball" embedded />}
            {tab === 'box-scores' && <BoxScoresTab boxScores={boxScores} onRefresh={refreshBoxScores} />}
            {tab === 'agents' && (
              <Suspense fallback={<div className="flex items-center justify-center h-64 text-zinc-500"><Loader2 className="animate-spin mr-2" size={20} /> Loading Agent Builder...</div>}>
                <div className="-mx-5 -mb-5 flex-1" style={{ minHeight: 'calc(100vh - 140px)' }}>
                  <CoachingCanvas />
                </div>
              </Suspense>
            )}
            {tab === 'settings' && (
              <SettingsTab
                settings={settings}
                onSave={handleSaveSettings}
                saving={saving}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
